import { cloneDeep } from 'lodash-es';
import { v4 as guid } from 'uuid';
import { formatTStoDATE } from '@platform/shared/lib/datetime';
import { roundNumber } from '@platform/shared/lib/round-number';
import { UserStore } from '@platform/store/index.js';

export { roundNumber };

/**
 * Форматирует число под ЦЕЛОЕ
 * @param params - число
 * @returns { string }
 */
export function integerFormatter(params) {
    let value;
    if (typeof params === 'object') {
        value = params?.value;
    } else {
        value = params;
    }
    return !isNaN(+value) ? formatNumber(+value, 0, 0) : null;
}

/**
 * Форматирует число под ЧИСЛО с 2-мя знаками после запятой (для СУММ и ЦЕН)
 * @param params - число
 * @returns { string }
 */
export function currencyFormatter(params) {
    let value;
    if (!!params && typeof params === 'object') {
        value = params.value;
    } else {
        value = params;
    }
    return !isNaN(+value) ? formatNumber(+value, 2, 2) : null;
}

/**
 * Форматирует число под ПРОЦЕНТ (с 1-м знаком после запятой)
 * @param params - число
 * @returns { number }
 */
export function percentFormatter(params) {
    let value;
    if (!!params && typeof params === 'object') {
        value = params.value;
    } else {
        value = params;
    }
    return value ? formatNumber(value, 1, 1) : null;
}

/**
 * Форматирует число под ЧИСЛО с 8-мя знаками после запятой (для КОЛ-ВА)
 * @param params - число
 * @returns { number }
 */
export function quantityFormatter(params) {
    return params.value != null
        ? formatNumber(params.value, params?.colDef?.cellRendererParams?.roundDigits ?? 8, null)
        : null;
}

/**
 * Форматирует из OBJECT -> STRING из поля label
 * @param params - число
 * @returns { string | null }
 */
export function objectFormatter(params) {
    return params?.data && params.colDef?.field && params.data[params.colDef.field]
        ? params.data[params.colDef.field].label
        : null;
}

/**
 * Форматирует из OBJECT -> STRING из поля files_count (file_store_parent_info)
 * @param params - число
 * @returns { number }
 */
export function fileFormatter(params) {
    return !!params && !!params.data && !!params.data.file_store_parent_info
        ? params.data.file_store_parent_info.files_count
        : null;
}

/**
 * Форматирует ЧИСЛО под необходимо минимальное кол-во знаков после запятой
 * @param number - число
 * @param roundDigits - кол-во знаков после запятой
 * @param minimumFractionDigits - минимальное кол-во знаков после запятой
 * @returns { string }
 */
export function formatNumber(number, roundDigits, minimumFractionDigits) {
    var mult;
    var result;
    if (roundDigits !== null && roundDigits > 0) {
        result = roundNumber(number, roundDigits);
        // mult = Math.pow(10, roundDigits);
        // result = Math.round(number * mult) / mult;
    } else if (roundDigits !== null && roundDigits === 0) {
        result = Math.trunc(number);
        // } else if (roundDigits !== null && roundDigits >= 0) {
        //   result = (Math.round(number * 100) / 100);
    } else {
        result = number;
    }

    if (!minimumFractionDigits) {
        return result.toLocaleString('ru-RU', { maximumFractionDigits: 8 });
    } else {
        return result.toLocaleString('ru-RU', {
            minimumFractionDigits: minimumFractionDigits,
            maximumFractionDigits: 8,
        });
    }
}

/**
 * Преобразует входной параметр (строку или число) в Число с округлением (если задано кол-во знаков после запятой)
 * либо возвращает NULL, если входной параметр не преобразуется в Число
 * @param value - число
 * @param roundDigits {number | null | undefined} [кол-во знаков после запятой]
 * @param returnNumberOnly {boolean=false}
 * @returns { number|null }
 */
export function getNumberFromX(value, roundDigits = null, returnNumberOnly) {
    if (value == null) return returnNumberOnly ? 0 : null;
    let result;
    if (typeof value === 'number') {
        if (isNaN(value)) return returnNumberOnly ? 0 : null;
        if (roundDigits !== null) {
            result = roundNumber(value, roundDigits);
        } else {
            result = value;
        }
    } else if (typeof value === 'string') {
        const strNum = value.replace(/ /g, '').replace(/\s/g, '').replace(',', '.');
        if (isNaN(parseFloat(strNum))) return returnNumberOnly ? 0 : null;
        if (roundDigits !== null) {
            result = roundNumber(parseFloat(strNum), roundDigits);
        } else {
            result = parseFloat(strNum);
        }
    }
    return result != null ? result : returnNumberOnly ? 0 : null;
}

/**
 * Возвращает объект под стандартный формат представления (ID из БД)
 * @param value {string | null} - значение
 * @param name {string | null} - наименование
 * @param label {string | null} - отображаемое наименование
 * @returns {{name: (string|null), label: (string|null), value: (string|null)}}
 */
export function getIdItem(value, name = null, label = null) {
    return { value: value, name: name, label: label ? label : name };
}

/**
 * поиск ID по Наименованию в данных справочника
 *  автодобавление в справочник идет без учета Родителя, все добавляется в корневик
 *  @param data
 *  @param name
 *  @param autoAddToReference
 *  @param saveFunc
 *  @return object в формате: {label, value, name}
 */
export function getReferenceItemByName(data, name, autoAddToReference = true, saveFunc = null) {
    if (!data || !name) {
        return {
            label: null,
            value: null,
            name: null,
        };
    }
    name = name.trim();
    const foundIndex = data.findIndex((row) => (row.name ? row.name.toLowerCase() === name.toLowerCase() : false));
    if (foundIndex >= 0) {
        return data[foundIndex];
    } else {
        const item = {
            label: name,
            value: guid().toLocaleLowerCase(),
            name: name,
        };

        if (autoAddToReference) {
            data.push(item);
            if (saveFunc) {
                const itemToSave = {
                    id: item.value,
                    name: item.name,
                };
                // Добавление нового item-а в список позиций справочника (чтобы новая позиция была сразу доступна для выбора)
                //todo Если тут будет ошибка при записи, то мы ее не отловим (async и await тут не добавлен) - доделать !!!!!!!!!!!!!!!!!!!!
                saveFunc(itemToSave, true);
            }
        }

        return item;
    }
}

/**
 * поиск объекта для поля по ID в данных справочника
 *  автодобавление в справочник идет без учета Родителя, все добавляется в корневик
 *  @param data
 *  @param id
 *  @return object в формате: {label, value, name}
 */
export function getReferenceItemByID(data, id) {
    if (!data || !id) {
        if (id !== 0 && id !== false) {
            return {
                label: null,
                value: null,
                name: null,
            };
        }
    }
    if (id !== 0 && id !== false) {
        id = id.trim();
    }
    const foundIndex = data.findIndex((row) => row.value === id);
    if (foundIndex >= 0) {
        return data[foundIndex];
    }
    return null;
}

/** НЕ ИСПОЛЬЗУЕТСЯ ПОКА
 * Поиск ID по Наименованию в данных справочника
 *  автодобавление в справочник идет без учета Родителя, все добавляется в корневик
 *  NOTE: может добавить item в параметр data!
 *  @param data - данные справочника
 *  @param findFields - структура для поиска
 *  [
 *      { field: fieldName1, value: value1 }
 *      { field: fieldName2, value: value2 }
 *      ...
 *  ]
 *  @param autoAddToReference - true для автодобавления в справочник в случае отсутствия
 *  @param saveFunc - функция для сохранения новой позиции в справочник
 *  @param saveFields - поля для записи
 *  {
 *      _label: label, - поле label
 *      _name: name, - поле name
 *      fieldName1: value1,
 *      fieldName2: value2,
 *      ...
 *  }
 *  @return object в формате: {label, value, name}
 */
export async function getReferenceItemByParams(
    data,
    findFields,
    saveFields,
    autoAddToReference = false,
    saveFunc = null
) {
    if (!data || data.length < 1 || !findFields) {
        return {
            label: null,
            value: null,
            name: null,
        };
    }

    const funcFind = function (item) {
        for (const filter of findFields) {
            if (item[filter.field] !== filter.value) return false;
        }
        return true;
    };

    const foundIndex = data.findIndex(funcFind);
    if (foundIndex >= 0) {
        return data[foundIndex];
    } else {
        const item = {
            label: null,
            value: null,
            name: null,
        };

        if (!!autoAddToReference && !!saveFunc) {
            item.label = saveFields._label;
            item.name = saveFields._name;
            item.value = guid().toLocaleLowerCase();

            delete saveFields._label;
            delete saveFields._name;

            const itemToSave = cloneDeep(saveFields);
            itemToSave.id = item.value;

            // Добавление нового item-а в список позиций справочника (чтобы новая позиция была сразу доступна для выбора)
            if (await saveFunc(itemToSave, true)) {
                data.push(item);
            } else {
                return {
                    label: null,
                    value: null,
                    name: null,
                };
            }
        }

        return item;
    }
}

/**
 * !!! ТОЛЬКО ДЛЯ ГРИДОВ используется спец.поле client_grid_hierarchy_column (это поле ИЗМЕНЯЕТСЯ)!!!
 * Возвращает список (массив) родительских объектов для текущего объекта
 * Необходимо для построение дерева в ГРИДе
 * @param data - весь массив данных
 * @param item - текущий объект для которого идет поиск родителей в массиве data
 * @param result - результирующий массив (найденные родители)
 * @param parentField - имя поля обозначающего родителя
 * @param searchByField - имя поля, с которым сравнивать поле parentField
 * @param groupFieldName - имя поля для группирвоки (ранее был по умолчанию NAME, сейчас ID - везде в иходниках ID и возможно этот параметр уже НЕ нужен)
 * @returns {T[]}
 */
//!!! ТОЛЬКО ДЛЯ ГРИДОВ используется спец.поле client_grid_hierarchy_column (это поле ИЗМЕНЯЕТСЯ)!!!
export function getParents(data, item, result, parentField, searchByField = null, groupFieldName = 'id') {
    if (item.client_grid_hierarchy_column?.length) return item.client_grid_hierarchy_column;
    // TODO тут нужна проверка в row[searchByField] является ли это объектом или стрингом (пока что сделал только так, потому что там объект)
    if (searchByField) {
        if (item[parentField] != null && typeof item[parentField] === 'object' && !!item[parentField].value) {
            const foundIndex = data.findIndex(
                (row) => !!row.is_group && row[searchByField].value === item[parentField].value
            );
            if (foundIndex >= 0) {
                if (!data[foundIndex]?.client_grid_hierarchy_column?.length) {
                    result = result.concat(
                        getParents(data, data[foundIndex], result, parentField, searchByField, groupFieldName)
                    );
                    data[foundIndex].client_grid_hierarchy_column = cloneDeep(result);
                } else {
                    result = data[foundIndex]?.client_grid_hierarchy_column;
                }
            }
        } else if (item[parentField] != null && typeof item[parentField] === 'string' && item[parentField].length > 0) {
            const foundIndex = data.findIndex(
                (row) => !!row.is_group && row[searchByField].value === item[parentField]
            );
            if (foundIndex >= 0) {
                if (!data[foundIndex]?.client_grid_hierarchy_column?.length) {
                    result = result.concat(
                        getParents(data, data[foundIndex], result, parentField, searchByField, groupFieldName)
                    );
                    data[foundIndex].client_grid_hierarchy_column = cloneDeep(result);
                } else {
                    result = data[foundIndex]?.client_grid_hierarchy_column;
                }
            }
        }
    } else if (item[parentField] != null && typeof item[parentField] === 'object' && !!item[parentField].value) {
        const foundIndex = data.findIndex((row) => row.id === item[parentField].value);
        if (foundIndex >= 0) {
            if (!data[foundIndex]?.client_grid_hierarchy_column?.length) {
                result = result.concat(
                    getParents(data, data[foundIndex], result, parentField, searchByField, groupFieldName)
                );
                data[foundIndex].client_grid_hierarchy_column = cloneDeep(result);
            } else {
                result = data[foundIndex]?.client_grid_hierarchy_column;
            }
        }
    } else if (item[parentField] != null && typeof item[parentField] === 'string' && item[parentField].length > 0) {
        const foundIndex = data.findIndex((row) => row.id === item[parentField]);
        if (foundIndex >= 0) {
            if (!data[foundIndex]?.client_grid_hierarchy_column?.length) {
                result = result.concat(
                    getParents(data, data[foundIndex], result, parentField, searchByField, groupFieldName)
                );
                data[foundIndex].client_grid_hierarchy_column = cloneDeep(result);
            } else {
                result = data[foundIndex]?.client_grid_hierarchy_column;
            }
        }
    }
    result = result.concat([item[groupFieldName]]);
    return result;
}

/**
 * Возвращает дерево объектов (массив с вложенными объектами)
 * Необходимо для построение дерева для выбора в полях на формах или в ячейках грида
 * @param data - весь массив данных
 * @param parent - текущий объект (или null, если стоится все дерево по data) для которого ищем дочерние объекты
 * @param parentField - имя поля обозначающего родителя
 * @param canSelectParents - true, если можно выбирать родительские node, иначе можно выбирать только конечные node
 * @returns {T[]} возвращает массив вида:
 *  {
 *      label: - отображаемое наименование
 *      value: - значение
 *      name: - наименование
 *      parentId: - ID родителя
 *      selectable: - признак возможности выбора элемента
 *      children: [{}, {}, ...] - массив дочерних объектов идентичной структуры
 *  }
 */
export function getChildren(data, parent, parentField, canSelectParents = true) {
    let children;
    const result = [];

    if (parent) {
        children = data.filter((item) => !!item[parentField] && item[parentField].value === parent.id);
    } else {
        children = data.filter((item) => !item[parentField] || !item[parentField].value);
    }

    let subchildren = [];
    children.forEach((item) => {
        result.push({
            label: item.name,
            value: item.id,
            name: item.name,
            parentId: item[parentField] ? item[parentField].value : null,
        });
        subchildren = getChildren(data, item, parentField, canSelectParents);
        if (!!subchildren && subchildren.length > 0) {
            result[result.length - 1].children = subchildren.slice();
            result[result.length - 1].selectable = canSelectParents !== false;
        } else {
            result[result.length - 1].selectable = true;
        }
    });

    return result.slice();
}

/**
 * Возвращает дерево объектов (массив с вложенными объектами)
 * Необходимо для построение дерева для выбора в полях на формах или в ячейках грида
 * @param data - весь массив данных
 * @param parent - текущий объект (или null, если стоится все дерево по data) для которого ищем дочерние объекты
 * @param parentField - имя поля обозначающего родителя
 * @param canSelectParents - true, если можно выбирать родительские node, иначе можно выбирать только конечные node
 * @param dataStructure - структура children
 * @returns {T[]} возвращает массив вида:
 *  {
 *      label: - отображаемое наименование
 *      value: - значение
 *      name: - наименование
 *      parentId: - ID родителя
 *      selectable: - признак возможности выбора элемента
 *      children: [{}, {}, ...] - массив дочерних объектов идентичной структуры
 *  }
 */
export function getChildrenDataStructure(data, parent, parentField, canSelectParents = true, dataStructure) {
    let children;
    const result = [];

    if (parent) {
        children = data.filter((item) => !!item[parentField] && item[parentField].value === parent.id);
    } else {
        children = data.filter((item) => !item[parentField] || !item[parentField].value);
    }

    let subchildren = [];
    children.forEach((item) => {
        const newItem = {
            label: item.name,
            value: item.id,
            name: item.name,
            parentId: item[parentField] ? item[parentField].value : null,
        };
        if (dataStructure?.length) {
            dataStructure.forEach((name) => {
                newItem[name] = item?.[name];
            });
        }
        result.push(newItem);
        subchildren = getChildrenDataStructure(data, item, parentField, canSelectParents, dataStructure);
        if (!!subchildren && subchildren.length > 0) {
            result[result.length - 1].children = subchildren.slice();
            result[result.length - 1].selectable = canSelectParents !== false;
        } else {
            result[result.length - 1].selectable = true;
        }
    });

    return result.slice();
}

export async function contextMenuOnChange(gridApi, allCellRanges, changeFunc, params) {
    const updateRows = [];
    const nodes = [];
    gridApi.forEachNodeAfterFilter((node) => nodes.push(node));
    for (const node of nodes) {
        if (!node.data) continue;
        const data = node.data;
        for (const cellRanges of allCellRanges) {
            let rangeMin, rangeMax;
            if (!!cellRanges && cellRanges.startRow.rowIndex < cellRanges.endRow.rowIndex) {
                rangeMin = cellRanges.startRow.rowIndex;
                rangeMax = cellRanges.endRow.rowIndex;
            } else if (cellRanges) {
                rangeMax = cellRanges.startRow.rowIndex;
                rangeMin = cellRanges.endRow.rowIndex;
            }
            if (rangeMax != null && rangeMin != null && rangeMin <= node.rowIndex && rangeMax >= node.rowIndex) {
                const columns = cellRanges.columns;
                for (const item of columns) {
                    await changeFunc(data, item, params, node);
                    updateRows.push(data);
                }
            }
        }
    }
    gridApi.applyTransaction({ update: updateRows });
    gridApi.clearRangeSelection();
    gridApi.onFilterChanged();
}

// для создания нового объекта выделенных ячеек в гриде
export function copyCellRange(cellRange) {
    const allCellRanges = [];
    for (const item of cellRange) {
        const mass = [];
        for (const column of item.columns) {
            mass.push(column);
        }
        allCellRanges.push({
            startRow: {
                rowIndex: item.startRow.rowIndex,
            },
            endRow: {
                rowIndex: item.endRow.rowIndex,
            },
            columns: mass,
        });
    }
    return allCellRanges;
}

export function showFile(blob, mimetype, filename) {
    // It is necessary to create a new blob object with mime-type explicitly set
    // otherwise only Chrome works like it should
    const newBlob = new Blob([blob], { type: mimetype });

    // IE doesn't allow using a blob object directly as link href
    // instead it is necessary to use msSaveOrOpenBlob
    // if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    //     window.navigator.msSaveOrOpenBlob(newBlob);
    //     return;
    // }

    // For other browsers:
    // Create a link pointing to the ObjectURL containing the blob.
    // const data = window.URL.createObjectURL(newBlob);
    const link = document.createElement('a');
    link.target = '_blank';
    const data = window.URL.createObjectURL(newBlob);
    if (mimetype !== 'application/pdf') {
        // data = "data:" + mimetype + ";base64," + blob;
        link.download = filename ? filename : 'pusk_report_' + formatTStoDATE(new Date(), 'DD_MM_YYYY');
    }
    link.href = data;
    link.click();
    // setTimeout(function(){
    //     // For Firefox it is necessary to delay revoking the ObjectURL
    //     window.URL.revokeObjectURL(data);
    //     // window.open(URL.createObjectURL(response.data));
    // }, 200);
}

//Возвращает Сумму НДС и Сумму рассчитанную от "главной"
function getSumFromMain(mainSum, ndsType, operation) {
    const ndsRef = getItemNDS(ndsType);
    const value = roundNumber(
        (mainSum * ndsRef.vatRate) / (100 + (operation > 0 && !ndsRef.include ? 0 : ndsRef.vatRate)),
        2
    );
    return [value, ndsRef.include ? mainSum : mainSum + operation * value];
}

/**
 * Расчет "Суммы НДС" и "Суммы с НДС"
 * @param data {Object} - исходные данные, туда же идет запись результата
 * @param colId {String} - имя столбца инициирующего изменения (если изменен столбец "Сумма НДС", то изменяем тольуо столбец "Суммы с НДС")
 * @param fieldsMap {Object} - "маппинг" полей участвующих в расчетах. По умолчанию заполняется стандартными именами полей.
 * @param calcOnlyFields {Object} - поля требующие перерасчета. По умолчанию идет стандартный перерасчет.
 */
export function calculateNDSBlock(data, colId, fieldsMap = {}, calcOnlyFields = null) {
    fieldsMap = {
        //стандартные поля
        quantity: 'quantity',
        price: 'price',
        ndsType: 'nds_type',
        sum: 'sum',
        ndsSum: 'nds_sum',
        total: 'total',
        discount: 'discount',
        ...fieldsMap,
    };

    const quantity = getNumberFromX(data[fieldsMap['quantity']], 8, true);
    let price = getNumberFromX(data[fieldsMap['price']], 2, true);
    const discount = getNumberFromX(data[fieldsMap['discount']], 2, true) || 0;
    let sum = getNumberFromX(data[fieldsMap['sum']], 2, true);
    let ndsSum = getNumberFromX(data[fieldsMap['ndsSum']], 2, true);
    let total = getNumberFromX(data[fieldsMap['total']], 2, true);
    const ndsType = data[fieldsMap['ndsType']]?.value ? data[fieldsMap['ndsType']].value : 0;

    if (colId === fieldsMap['total'] || colId === fieldsMap['ndsType']) {
        [ndsSum, sum] = getSumFromMain(total, ndsType ?? 0, -1);
        price = quantity ? sum / quantity : 0;
    } else if (
        colId === fieldsMap['sum'] ||
        colId === fieldsMap['price'] ||
        colId === fieldsMap['quantity'] ||
        colId === fieldsMap['discount']
    ) {
        if (colId === fieldsMap['price'] || colId === fieldsMap['quantity'] || colId === fieldsMap['discount'])
            sum = roundNumber(price * quantity - discount, 2);

        [ndsSum, total] = getSumFromMain(sum, ndsType ?? 0, 1);

        if (colId === fieldsMap['sum']) price = quantity ? sum / quantity : 0;
    }

    data[fieldsMap['quantity']] =
        !calcOnlyFields || calcOnlyFields.includes('quantity')
            ? getNumberFromX(quantity, 8, true)
            : data[fieldsMap['quantity']];
    data[fieldsMap['price']] =
        !calcOnlyFields || calcOnlyFields.includes('price') ? getNumberFromX(price, 2, true) : data[fieldsMap['price']];
    data[fieldsMap['sum']] =
        !calcOnlyFields || calcOnlyFields.includes('sum') ? getNumberFromX(sum, 2, true) : data[fieldsMap['sum']];
    data[fieldsMap['ndsSum']] =
        !calcOnlyFields || calcOnlyFields.includes('ndsSum')
            ? getNumberFromX(ndsSum, 2, true)
            : data[fieldsMap['ndsSum']];
    data[fieldsMap['total']] =
        !calcOnlyFields || calcOnlyFields.includes('total') ? getNumberFromX(total, 2, true) : data[fieldsMap['total']];
}

export function getListNDS(country = null) {
    const COUNTRY = country ?? UserStore?.userState?.sysOptions?.platform$contragent.COUNTRY;

    //============================================================================================
    //!!!!! ВАЖНО !!!!!
    //ВСЕГДА ДОЛЖНА БЫТЬ ПОСЛЕДОВАТЕЛЬНОСТЬ
    // - первым (индекс 0) идет ВСЕГДА "без НДС"
    // - далее ВСЕГДА идут ПАРЫ НДС, причем СНАЧАЛА "НДС в т.ч. ...", а затем "НДС сверху ..."
    //============================================================================================

    switch (COUNTRY) {
        case 'KZ':
            return [
                { label: 'без НДС', value: 0, name: 'без НДС', vatRate: 0, include: true },
                //до 2026
                { label: 'НДС в т.ч. 12%', value: 1, name: 'НДС в т.ч. 12%', vatRate: 12, include: true },
                { label: 'НДС сверху 12%', value: 2, name: 'НДС сверху 12%', vatRate: 12, include: false },
                //c 2026
                { label: 'НДС в т.ч. 16%', value: 3, name: 'НДС в т.ч. 16%', vatRate: 16, include: true },
                { label: 'НДС сверху 16%', value: 4, name: 'НДС сверху 16%', vatRate: 16, include: false },
                { label: 'НДС в т.ч. 5%', value: 5, name: 'НДС в т.ч. 5%', vatRate: 5, include: true },
                { label: 'НДС сверху 5%', value: 6, name: 'НДС сверху 5%', vatRate: 5, include: false },
                //c 2027
                { label: 'НДС в т.ч. 10%', value: 7, name: 'НДС в т.ч. 10%', vatRate: 10, include: true },
                { label: 'НДС сверху 10%', value: 8, name: 'НДС сверху 10%', vatRate: 10, include: false },
            ];
        case 'UZ':
            return [
                { label: 'без НДС', value: 0, name: 'без НДС', vatRate: 0, include: true },
                { label: 'НДС в т.ч. 12%', value: 1, name: 'НДС в т.ч. 12%', vatRate: 12, include: true },
                { label: 'НДС сверху 12%', value: 2, name: 'НДС сверху 12%', vatRate: 12, include: false },
            ];
        case 'RU':
        default:
            return [
                { label: 'без НДС', value: 0, name: 'без НДС', vatRate: 0, include: true },
                //с 2026
                { label: 'НДС в т.ч. 22%', value: 9, name: 'НДС в т.ч. 22%', vatRate: 22, include: true },
                { label: 'НДС сверху 22%', value: 10, name: 'НДС сверху 22%', vatRate: 22, include: false },
                //до 2026
                { label: 'НДС в т.ч. 20%', value: 1, name: 'НДС в т.ч. 20%', vatRate: 20, include: true },
                { label: 'НДС сверху 20%', value: 2, name: 'НДС сверху 20%', vatRate: 20, include: false },
                //постоянная
                { label: 'НДС в т.ч. 10%', value: 3, name: 'НДС в т.ч. 10%', vatRate: 10, include: true },
                { label: 'НДС сверху 10%', value: 4, name: 'НДС сверху 10%', vatRate: 10, include: false },
                //с 2025
                { label: 'НДС в т.ч. 7%', value: 5, name: 'НДС в т.ч. 7%', vatRate: 7, include: true },
                { label: 'НДС сверху 7%', value: 6, name: 'НДС сверху 7%', vatRate: 7, include: false },
                { label: 'НДС в т.ч. 5%', value: 7, name: 'НДС в т.ч. 5%', vatRate: 5, include: true },
                { label: 'НДС сверху 5%', value: 8, name: 'НДС сверху 5%', vatRate: 5, include: false },
            ];
    }
}

export function getDefaultItemNDS(include ) {
    const listNDS = getListNDS();
    const defaultNDSValue = UserStore?.userState?.sysOptions?.platform$contragent.DEFAULT_NDS;
    const defaultNDSIndex = listNDS.findIndex(i => i.value === (defaultNDSValue ?? 9)) ?? 1;
    const defaultNDS = listNDS.find((i) => i.value === defaultNDSValue) ?? listNDS[defaultNDSIndex];

    if (include == null || defaultNDSIndex === 0 || !!include === !!defaultNDS.include) {
        return defaultNDS;
    }
    if (!!include !== !!defaultNDS.include) {
        return include
            ? listNDS[defaultNDSIndex - 1]
            : defaultNDSIndex + 1 < listNDS?.length
              ? listNDS[defaultNDSIndex + 1]
              : listNDS[defaultNDSIndex];
    }
}

export function getItemNDS(ndsType) {
    return getListNDS()?.find(
        (i) => i.value === +(ndsType ?? UserStore?.userState?.sysOptions?.platform$contragent?.DEFAULT_NDS ?? 9));
}

export function getListPeriod() {
    return [
        { label: 'по дням', value: 'day', name: 'по дням' },
        { label: 'по месяцам', value: 'month', name: 'по месяцам' },
        { label: 'по кварталам', value: 'quarter', name: 'по кварталам' },
        { label: 'по годам', value: 'year', name: 'по годам' },
    ];
}

export function getItemPeriod(period) {
    return getListPeriod().find((p) => p.value === period);
}

/**
 * Проверки корректности ИНН
 * @param value {string}
 * @return {boolean}
 */
export function isValidInn(value) {
    if (!value || typeof value !== 'string' || value.trim() === '' || value.trim() === '0') return false;
    if (value.match(/\D/)) return false;

    const inn = value.match(/(\d)/g);
    let sumInn;

    if (inn.length === 10) {
        sumInn =
            2 * inn[0] +
            4 * inn[1] +
            10 * inn[2] +
            3 * inn[3] +
            5 * inn[4] +
            9 * inn[5] +
            4 * inn[6] +
            6 * inn[7] +
            8 * inn[8];
        return sumInn !== 0 && inn[9] === String((sumInn % 11) % 10);
    } else if (inn.length === 12) {
        sumInn =
            7 * inn[0] +
            2 * inn[1] +
            4 * inn[2] +
            10 * inn[3] +
            3 * inn[4] +
            5 * inn[5] +
            9 * inn[6] +
            4 * inn[7] +
            6 * inn[8] +
            8 * inn[9];
        return (
            sumInn !== 0 &&
            inn[10] === String((sumInn % 11) % 10) &&
            inn[11] ===
                String(
                    ((3 * inn[0] +
                        7 * inn[1] +
                        2 * inn[2] +
                        4 * inn[3] +
                        10 * inn[4] +
                        3 * inn[5] +
                        5 * inn[6] +
                        9 * inn[7] +
                        4 * inn[8] +
                        6 * inn[9] +
                        8 * inn[10]) %
                        11) %
                        10
                )
        );
    }

    return false;
}

/**
 * Контрольная сумма БИН/ИИН Казахстана (12 цифр).
 * Держать в паре с platform-server-node/libs/common/src/ext_function.js.
 * @param value {string}
 * @return {boolean}
 */
export function isValidBin(value) {
    if (!value || typeof value !== 'string' || !/^\d{12}$/.test(value)) return false;
    const digits = value.split('').map((digit) => Number(digit));
    const weights1 = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    const weights2 = [3, 4, 5, 6, 7, 8, 9, 10, 11, 1, 2];
    const checksum = (weights) => {
        let sum = 0;
        for (let i = 0; i < 11; i++) sum += digits[i] * weights[i];
        return sum % 11;
    };
    let control = checksum(weights1);
    if (control === 10) {
        control = checksum(weights2);
        if (control === 10) return false;
    }
    return control === digits[11];
}

/** Номер подходит как российский ИНН или казахстанский БИН. */
export function isValidPartyId(value) {
    return isValidInn(value) || isValidBin(value);
}

/** Ручной ввод карточки: как на бою, достаточно 10 или 12 цифр. Контрольная сумма нужна поиску и распознаванию. */
export function hasPartyIdLength(value) {
    return typeof value === 'string' && /^(\d{10}|\d{12})$/.test(value.trim());
}

/** Подпись поля номера: для Казахстана в нём лежит и ИНН, и БИН. */
export function partyIdLabel() {
    return UserStore?.userState?.sysOptions?.platform$contragent?.COUNTRY === 'KZ' ? 'ИНН/БИН' : 'ИНН';
}

/** Подпись поиска. Для Казахстана справочник уже не ФНС. */
export function partySearchLabel() {
    return partyIdLabel() === 'ИНН/БИН' ? 'Поиск организации или ИП' : 'Поиск организации или ИП по базе ФНС';
}

export function validateRs(rs, bik, error, allowEmptyBik = false) {
    let result = false;

    if (allowEmptyBik || validateBik(bik, error)) {
        if (typeof rs === 'number') {
            rs = rs.toString();
        } else if (typeof rs !== 'string') {
            rs = '';
        }
        if (!rs.length) {
            error.code = 1;
            error.message = 'Расчетный счет должен быть указан';
        } else if (/[^0-9]/.test(rs)) {
            error.code = 2;
            error.message = 'Расчетный счет может состоять только из цифр';
        } else if (rs.length !== 20) {
            error.code = 3;
            error.message = 'Расчетный счет должен состоять из 20 цифр';
        } else {
            if (allowEmptyBik) {
                return result;
            }
            const bikRs = bik.toString().slice(-3) + rs;
            let checksum = 0;
            const coefficients = [7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1, 3, 7, 1];
            for (const i in coefficients) {
                checksum += coefficients[i] * (bikRs[i] % 10);
            }
            if (checksum % 10 === 0) {
                result = true;
            } else {
                error.code = 4;
                error.message = 'Неправильное контрольное число';
            }
        }
    }
    return result;
}

function validateBik(bik, error) {
    let result = false;
    if (typeof bik === 'number') {
        bik = bik.toString();
    } else if (typeof bik !== 'string') {
        bik = '';
    }
    if (!bik.length) {
        error.code = 1;
        error.message = 'БИК пуст';
    } else if (/[^0-9]/.test(bik)) {
        error.code = 2;
        error.message = 'БИК может состоять только из цифр';
    } else if (bik.length !== 9) {
        error.code = 3;
        error.message = 'БИК может состоять только из 9 цифр';
    } else if (bik[6] === '0' && bik[7] === '0') {
        error.code = 0;
    } else {
        result = true;
    }
    return result;
}

export function validateEmail(email) {
    if (email.match(/[А-Я]/) || email.match(/[а-я]/) || email.match(/[+]/) || email.match(/[A-Z]/)) return false;

    if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/.test(email)) return false;

    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export function isIntNumeric(value) {
    return /^-?\d+$/.test(value);
}

/**
 * Убирает из строки все символы кроме ЧИСЛА и ТОЧКИ - число только остается
 * @param value - строка "грязная"
 * @param comma - если true то разделитель дроби "," (false = "."), а если NULL то
 * @return {string|null} - строка "чистая" (только ЧИСЛА и ТОЧКИ)
 */
export function getNumericFromString(value, comma) {
    if (!value || typeof value !== 'string') return null;
    return comma != null
        ? !comma
            ? value.replace(/[^0-9.-]/g, '')
            : value.replace(/[^0-9,-]/g, '')
        : value.replace(/[^0-9.,-]/g, '').replace(',', '.');
}

export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Возвращает name из title / date
 * @param params - title
 * @param params - date
 * @returns { string }
 */
export function formatItemToTitle({ title, date }) {
    return `${title ? title : 'Без наименования'} (${date ? formatTStoDATE(date) : 'без даты'})`;
}

/*
 * Обрезание строки в JavaScript
 * */
export function truncateString(str, num) {
    if (str.length <= num) {
        return str;
    }
    return str.slice(0, num) + '...';
}

/**
 * Возвращает Array из переданного параметра JSON, проверяет тип параметра для преобразования из String, Array, Object
 * @param params {String|Array|Object|null}
 * @returns { Array }
 */
export function getArrayFromJson(value) {
    let result;
    if (typeof value === 'string') {
        result = JSON.parse(value);
        return result ? (Array.isArray(result) ? result : [result]) : [];
    } else {
        return value ? (Array.isArray(value) ? value : [value]) : [];
    }
}

/*
 * Parse строку
 * */
export function jsonParse(value, isArray = false) {
    value = typeof value === 'string' ? JSON.parse(value) : value;

    if (isArray && value) value = Array.isArray(value) ? value : [value];
    return value;
}

/*
 * Stringify элемент
 * Проверяем, что value не null, чтобы в json не было записи такого вида 'null'
 * */
export function jsonStringify(value, isArray = false) {
    if (typeof value === 'string') value = jsonParse(value);

    if (!value || Object.keys(value).length === 0 || (Array.isArray(value) && value?.length === 0)) return null;
    if (isArray) value = Array.isArray(value) ? value : [value];
    return JSON.stringify(value);
}

/*
 * Склонение числительных в JS
 * Небольшая javascript функция для склонения числительных, в которой первый аргумент число, второй – массив с тремя элементам.
 * Пример
 * num_word(value, ['товар', 'товара', 'товаров']);
 * num_word(value, ['штука', 'штуки', 'штук']);
 * num_word(value, ['пара', 'пары', 'пар']);
 * num_word(value, ['рубль', 'рубля', 'рублей']);
 * Чтобы заполнить массив с формами склонений, нужно ответить на три вопроса:
 *    Каким будет слово для одной единицы?
 *    Каким будет слово для двух единиц?
 *    Каким будет слово для пяти единиц?
 * */
export function num_word(value, words) {
    if (!value) return;

    value = Math.abs(value) % 100;
    var num = value % 10;

    if (value > 10 && value < 20) return words[2];
    if (num > 1 && num < 5) return words[1];
    if (num === 1) return words[0];

    return words[2];
}

//  удалить только HTML-теги в строке
export function sanitize(txt) {
    const protos = document.body.constructor === window.HTMLBodyElement;
    const validHTMLTags =
        /^(?:a|abbr|acronym|address|applet|area|article|aside|audio|b|base|basefont|bdi|bdo|bgsound|big|blink|blockquote|body|br|button|canvas|caption|center|cite|code|col|colgroup|data|datalist|dd|del|details|dfn|dir|div|dl|dt|em|embed|fieldset|figcaption|figure|font|footer|form|frame|frameset|h1|h2|h3|h4|h5|h6|head|header|hgroup|hr|html|i|iframe|img|input|ins|isindex|kbd|keygen|label|legend|li|link|listing|main|map|mark|marquee|menu|menuitem|meta|meter|nav|nobr|noframes|noscript|object|ol|optgroup|option|output|p|param|plaintext|pre|progress|q|rp|rt|ruby|s|samp|script|section|select|small|source|spacer|span|strike|strong|style|sub|summary|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|track|tt|u|ul|var|video|wbr|xmp)$/i;

    const // This regex normalises anything between quotes
        normaliseQuotes = /=(["'])(?=[^\1]*[<>])[^\1]*\1/g,
        normaliseFn = function ($0, q, sym) {
            return $0.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        },
        replaceInvalid = function ($0, tag, off, txt) {
            var // Is it a valid tag?
                invalidTag =
                    (protos && document.createElement(tag) instanceof HTMLUnknownElement) || !validHTMLTags.test(tag),
                // Is the tag complete?
                isComplete = txt.slice(off + 1).search(/^[^<]+>/) > -1;

            return invalidTag || !isComplete ? '&lt;' + tag : $0;
        };

    txt = txt.replace(normaliseQuotes, normaliseFn).replace(/<(\w+)/g, replaceInvalid);

    const tmp = document.createElement('DIV');
    tmp.innerHTML = txt;

    return 'textContent' in tmp ? tmp.textContent : tmp.innerHTML;
}
